// src/hooks/useSpotQueue.js - VERSÃO CORRIGIDA
// ✅ Executa apenas 1 spot e libera playlist
// ✅ Deduplicação melhorada
// ✅ Controle de execução única

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  addWebSocketListener,
  removeWebSocketListener,
} from '../services/websocketSingleton';
import SynchronizerSpot from '../components/synchronizers/SynchronizerSpot';

export function useSpotQueue() {
  const [spotQueue, setSpotQueue] = useState([]);
  const [isPlayingSpot, setIsPlayingSpot] = useState(false);
  const syncSpotRef = useRef(null);
  const processedSpotsRef = useRef(new Set());
  const lastSpotTimeRef = useRef(0); // ✅ NOVO: Controle temporal

  // Inicializar SynchronizerSpot
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      let attempts = 0;
      while (!window.audioCoordinator && attempts < 50) {
        await new Promise((r) => setTimeout(r, 100));
        attempts += 1;
      }

      if (!isMounted) return;

      if (window.audioCoordinator) {
        syncSpotRef.current = new SynchronizerSpot(window.audioCoordinator);
        console.log('[useSpotQueue] ✅ SyncSpot criado');
      } else {
        console.warn('[useSpotQueue] ❌ AudioCoordinator não disponível');
      }
    };

    init();

    return () => {
      isMounted = false;
      if (syncSpotRef.current) {
        syncSpotRef.current.destroy?.();
        syncSpotRef.current = null;
      }
    };
  }, []);

  const updateLocalQueue = useCallback(() => {
    if (!syncSpotRef.current) return;
    const status = syncSpotRef.current.getStatus();
    setSpotQueue(status.queue);
    setIsPlayingSpot(status.isPlaying);
  }, []);

  // ✅ FUNÇÃO MELHORADA: Gera ID único e consistente
  const generateSpotId = useCallback((evento) => {
    // Usa combinação de campos para garantir unicidade
    const baseId = evento.id || evento.audioFile || evento.nome || 'unknown';
    const timestamp = evento.timestamp || Date.now();
    return `${baseId}-${timestamp}`;
  }, []);

  // ✅ NOVO: Limpar spots processados a cada minuto
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      // Limpa spots processados há mais de 2 minutos
      processedSpotsRef.current = new Set(
        Array.from(processedSpotsRef.current).filter(id => {
          const parts = id.split('-');
          const timestamp = parseInt(parts[parts.length - 1]);
          return (now - timestamp) < 120000; // 2 minutos
        })
      );
      console.log('[useSpotQueue] 🧹 Limpeza de spots processados:', processedSpotsRef.current.size);
    }, 60000); // A cada 1 minuto

    return () => clearInterval(cleanupInterval);
  }, []);

  // Listener de WebSocket com controle aprimorado
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'SPOT_READY' || data.type === 'PLAY_SPOT') {
          const evento = data.evento;
          if (!evento) {
            console.warn('[useSpotQueue] ⚠️ Evento sem dados');
            return;
          }

          const now = Date.now();
          
          // ✅ PROTEÇÃO 1: Evitar múltiplos spots no mesmo segundo
          if (now - lastSpotTimeRef.current < 1000) {
            console.log('[useSpotQueue] 🚫 Spot bloqueado - muito rápido (< 1s)');
            return;
          }

          // ✅ PROTEÇÃO 2: Gera ID único
          const spotId = generateSpotId(evento);

          // ✅ PROTEÇÃO 3: Deduplicação por ID
          if (processedSpotsRef.current.has(spotId)) {
            console.log('[useSpotQueue] 🚫 Spot duplicado ignorado:', spotId);
            return;
          }

          // ✅ PROTEÇÃO 4: Verifica se já está tocando um spot
          if (isPlayingSpot) {
            console.log('[useSpotQueue] 🚫 Spot bloqueado - já há um spot tocando');
            return;
          }

          console.log('[useSpotQueue] ✅ Adicionando spot único:', spotId);
          
          // Marca como processado
          processedSpotsRef.current.add(spotId);
          lastSpotTimeRef.current = now;

          // Adiciona à fila
          if (syncSpotRef.current) {
            syncSpotRef.current.addToQueue({
              ...evento,
              id: spotId,
              addedAt: now
            });
            updateLocalQueue();
          }
        }
      } catch (error) {
        console.error('[useSpotQueue] ❌ Erro ao processar mensagem:', error);
      }
    };

    addWebSocketListener(handleMessage, 'useSpotQueue');

    return () => {
      removeWebSocketListener(handleMessage, 'useSpotQueue');
    };
  }, [updateLocalQueue, generateSpotId, isPlayingSpot]); // ✅ Dependência de isPlayingSpot

  // Polling para manter UI em sync (reduzido para economizar recursos)
  useEffect(() => {
    const interval = setInterval(updateLocalQueue, 1000); // ✅ Mudado de 500ms para 1s
    return () => clearInterval(interval);
  }, [updateLocalQueue]);

  const clearQueue = useCallback(() => {
    if (syncSpotRef.current) {
      syncSpotRef.current.clearQueue();
      updateLocalQueue();
      // Limpa também o registro de processados
      processedSpotsRef.current.clear();
      lastSpotTimeRef.current = 0;
      console.log('[useSpotQueue] 🧹 Fila limpa completamente');
    }
  }, [updateLocalQueue]);

  // ✅ NOVO: Debug info
  useEffect(() => {
    console.log('[useSpotQueue] 📊 Status:', {
      queueSize: spotQueue.length,
      isPlaying: isPlayingSpot,
      processedCount: processedSpotsRef.current.size
    });
  }, [spotQueue, isPlayingSpot]);

  return {
    spotQueue,
    isPlayingSpot,
    clearQueue,
  };
}